import { Keys } from '../../config';
import {
  Currency,
  CurrencyCross,
  CurrencyCrossObj,
  EStreamAllDataDTO,
  InstrumentCategoryEnum,
  InstrumentSubscriptionDataScope,
  ListedInstrument,
  ListedInstrumentLike,
} from '../../types';
import { isDefined, uniqueArray, uniqueArrayByReduceFn } from '../../utils';

/** We only require the market data scope in case of a currency */
const md = [InstrumentSubscriptionDataScope.MarketData];

/**
 * Utility class for currency exchange rate
 */
export class CurrencyExchangeRateRT {
  /**
   * Returns the exchange rate of the given currency crosses
   */
  async getExchangeRate({
    currencyCrosses,
    findInstruments,
  }: {
    currencyCrosses: Array<CurrencyCrossObj>;
    findInstruments(
      instruments: Array<ListedInstrument | ListedInstrumentLike>,
      dataScopes: Array<InstrumentSubscriptionDataScope>,
    ): Promise<Array<EStreamAllDataDTO>>;
  }): Promise<Map<CurrencyCross, number>> {
    if (!currencyCrosses.length) {
      return new Map<CurrencyCross, number>();
    }

    // get ric list of the currency crosses
    const ricList = currencyCrosses.map(({ base, quote }) =>
      this.getRicForEStreamRequest(base.toUpperCase() as Currency, quote.toUpperCase() as Currency),
    );
    const uniqueRicList = Array.from(new Set(ricList));

    const instrumentList = await findInstruments(this.getCurrencyCrossAsInstrument(uniqueRicList), md);

    if (instrumentList.length === 0) {
      throw new Error(`Invalid response`);
    }

    const result = new Map<CurrencyCross, number>();
    // find and map the exchange rates
    for (const ric of uniqueRicList) {
      // if this is a single currency cross, aka don't have to do the usd conversion
      if (this.isCurrencyCrossQueryable(ric)) {
        const instrument = instrumentList.find(instrument => instrument.ric === `${ric}`);
        if (instrument && this.isValidInstrument(instrument)) {
          const { base, quote } = this.getBaseAndQuoteCurrency(ric);
          // this is always defined because isValidInstrument make sure of that
          const rate = this.getCurrencyRateInQuoteCurrency(instrument, quote)!;

          result.set(`${base}${quote}=` as CurrencyCross, rate);
          result.set(`${quote}${base}=` as CurrencyCross, 1 / rate);
        } else if (instrument && !this.isValidInstrument(instrument)) {
          throw new Error(`Invalid instrument for ${ric}`);
        } else if (!instrument) {
          throw new Error(`EStream data is not loaded for ${ric}`);
        }
      } else {
        // USD conversion
        const { base, quote } = this.getBaseAndQuoteCurrency(ric);
        const baseCurrencyInUsdRic = this.getRicForEStreamRequest(base.toUpperCase() as Currency, 'USD' as Currency);
        const quoteCurrencyInUsdRic = this.getRicForEStreamRequest(quote.toUpperCase() as Currency, 'USD' as Currency);
        const baseCurrencyInUsd = instrumentList.find(instrument => instrument.ric === `${baseCurrencyInUsdRic}`);
        const quoteCurrencyInUsd = instrumentList.find(instrument => instrument.ric === `${quoteCurrencyInUsdRic}`);
        if (!baseCurrencyInUsd || !quoteCurrencyInUsd) {
          throw new Error(`Missing loaded instrument during USD conversion for ${ric}`);
        }

        // when we have the USD rates we can convert the original currency cross
        const baseCurrencyInUsdRate = this.getCurrencyRateInQuoteCurrency(baseCurrencyInUsd, 'USD' as Currency);
        const quoteCurrencyInUsdRate = this.getCurrencyRateInQuoteCurrency(quoteCurrencyInUsd, 'USD' as Currency);
        const rate = baseCurrencyInUsdRate / quoteCurrencyInUsdRate;
        result.set(ric, rate);
      }
    }
    return result;
  }

  getRicListOfCurrencyCrosses(currencyCrosses: Array<CurrencyCrossObj>): Array<CurrencyCross> {
    const list = currencyCrosses
      // .filter(({ base, quote }) => base !== quote)
      .map(({ base, quote }) => [
        { base, quote },
        { quote: base, base: quote },
      ])
      .flat()
      .map(({ base, quote }) => this.getRicForEStreamRequest(base, quote));

    return uniqueArray(list);
  }

  /**
   * Get ric list of the currency crosses
   */
  private getRicForEStreamRequest(base: Currency, quote: Currency): CurrencyCross {
    // USD conversion is tricky, we need to use the non USD currency
    if (quote === 'USD') {
      return `${base}=` as CurrencyCross;
    } else if (base === 'USD') {
      return `${quote}=` as CurrencyCross;
    }
    const ric = `${base}${quote}`;

    return `${ric}=` as CurrencyCross;
  }

  /**
   * Returns currency crosses as instruments to query from eStream
   * This function handles the case when the currency cross is not querable, so it returns instruments for USD conversion
   * @param ricList
   */
  private getCurrencyCrossAsInstrument(ricList: Array<string>): Array<ListedInstrumentLike> {
    const listedInstrumentLikes: Array<ListedInstrumentLike> = [];
    ricList.forEach(ric => {
      if (this.isCurrencyCrossQueryable(ric)) {
        listedInstrumentLikes.push({
          ric,
          mic: '',
          instrumentCategory: InstrumentCategoryEnum.Currency,
        });
      } else {
        const { base, quote } = this.getBaseAndQuoteCurrency(ric);
        const baseCurrencyInUsdRic = this.getRicForEStreamRequest(base.toUpperCase() as Currency, 'USD' as Currency);
        const quoteCurrencyInUsdRic = this.getRicForEStreamRequest(quote.toUpperCase() as Currency, 'USD' as Currency);
        listedInstrumentLikes.push(
          {
            ric: baseCurrencyInUsdRic,
            mic: '',
            instrumentCategory: InstrumentCategoryEnum.Currency,
          },
          {
            ric: quoteCurrencyInUsdRic,
            mic: '',
            instrumentCategory: InstrumentCategoryEnum.Currency,
          },
        );
      }
    });

    return listedInstrumentLikes.reduce(uniqueArrayByReduceFn('ric'), [] as Array<ListedInstrumentLike>);
  }

  /**
   * Returns true if the currency cross is querable meaning we can have a valid exchange rate from refinitiv
   *
   * That's needed because some currency crosses are returns real data (manually tested) while others not
   */
  private isCurrencyCrossQueryable(ric: string): boolean {
    // currency is something/USD, which tends to be querable
    if (ric.length === 4) {
      return true;
    } else return Keys.Currency.ValidCurrencyCrossesAgainstEstream.includes(ric);
  }

  /**
   * Refinitiv needs to have an X at the end of the currency cross to get the correct exchange rate
   * @param instrument
   */
  private extendRicWithX(instrument: ListedInstrumentLike): ListedInstrumentLike {
    return {
      ...instrument,
      ric: `${instrument.ric}${instrument.ric.endsWith('X') ? '' : 'X'}`,
    };
  }

  /**
   * Returns the base and quote currency from the currency cross ric
   */
  getBaseAndQuoteCurrency(ric: string): CurrencyCrossObj | never {
    if (ric.length === 4) {
      return {
        base: 'USD' as Currency,
        quote: ric.substr(0, 3) as Currency,
      };
    }
    const [_, base, quote] = ric.match(/([A-Z]{3})([A-Z]{3})/) || [];
    if (!base) {
      throw new Error(`Invalid currency cross, missing base ${ric}`);
    }
    if (!quote) {
      throw new Error(`Invalid currency cross, missing quote ${ric}`);
    }
    return {
      base: base as Currency,
      quote: quote as Currency,
    };
  }

  /**
   * Returns the exchange rate of an EStream instrument in the quote currency
   *
   * This function handles when refinitiv returns with the reciprocal currency cross, and returns the correct value
   * based on the quote currency
   */
  private getCurrencyRateInQuoteCurrency(instrument: EStreamAllDataDTO, quote: Currency): number | never {
    const rate = this.getCurrencyRate(instrument);
    if (!rate) {
      throw new Error(`Invalid rate for ${instrument.ric}`);
    }

    if (instrument.CURRENCY === quote) {
      return rate;
    }

    return Number((1 / rate).toFixed(6));
  }

  /**
   * Returns the exchange rate of an EStream instrument, by checking the PRIMACT_1, PRIMACT_2, HST_CLOSE, ASK fields
   */
  private getCurrencyRate(instrument: EStreamAllDataDTO): number | undefined {
    return (
      instrument.PRIMACT_1 ||
      instrument.PRIMACT_2 ||
      instrument.HST_CLOSE ||
      (instrument.ASK !== 'M' ? instrument.ASK : undefined)
    );
  }

  /**
   * Returns true if the EStream instrument has a valid currency field and a defined exchange rate
   */
  private isValidInstrument(instrument: EStreamAllDataDTO): boolean {
    return isDefined(instrument.CURRENCY) && isDefined(this.getCurrencyRate(instrument));
  }
}
